import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Dialog, DialogContent, List, ListItem,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import {
  compose, find, first, get, join, map, toNumber,
} from 'lodash/fp';

import { useModal } from '../../../../hooks';

const S = {
  Button: styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 40px;
    padding: 4px 8px 4px 12px;
    border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    border-radius: 4px;
    cursor: pointer;
    outline: none;
    :hover {
      border-color: ${({ theme }) => theme.palette.primary.main};
    }
    :focus {
      padding-left: 11px;
      padding-right: 7px;
      border: 2px solid ${({ theme }) => theme.palette.primary.main};
    }
  `,
  Value: styled.span`
    overflow: hidden;
    font-size: ${({ theme }) => theme.typography.pxToRem(14)};
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.grey[900]};
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  ExpandMore: styled(ExpandMore)`
    margin-left: auto;
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  Dialog: styled(Dialog)`
    .MuiPaper-root {
      margin: 40px;
    }
  `,
  DialogContent: styled(DialogContent)`
    padding: 0;
    :first-child {
      padding: 0;
    }
  `,
  Child: styled(ListItem)`
    display: block;
    overflow: hidden;
    padding: 16px 24px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: 1rem;
    line-height: 1.5;
    text-overflow: ellipsis;
    white-space: nowrap;
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
  `,
};

const ChildrenFilter = ({ value, options, onSelect }) => {
  const [open, openDialog, closeDialog] = useModal();

  const selectedTarget = useMemo(() => compose(
    (target) => (target
      ? join('', [
        target.clazzGroup
          ? `${target.clazzGroup.grade}학년 ${target.clazzGroup.clazzName}반 ${target.name}`
          : `${get('isUnspecifiedGroup', first(target.customGroups))
            ? '미지정' : get('name', first(target.customGroups))} ${target.name}`,
      ])
      : ''),
    find((option) => option.gcid === value),
  )(options), [options, value]);

  const selectChild = useCallback((event) => {
    closeDialog();

    onSelect(toNumber(event.currentTarget.dataset.value));
  }, [closeDialog, onSelect]);

  return (
    <>
      <S.Button type="button" onClick={openDialog}>
        <S.Value>
          {selectedTarget || '모든 자녀 보기'}
        </S.Value>
        <S.ExpandMore />
      </S.Button>
      <S.Dialog open={open} onClose={closeDialog}>
        <S.DialogContent>
          <List>
            <S.Child data-value="0" onClick={selectChild}>모든 자녀 보기</S.Child>
            {map((option) => (
              <S.Child key={option.id} data-value={option.gcid} onClick={selectChild}>
                {join('', [
                  option.clazzGroup
                    ? `${option.clazzGroup.grade}학년 ${option.clazzGroup.clazzName}반 ${option.name}`
                    : `${get('isUnspecifiedGroup', first(option.customGroups))
                      ? '미지정' : get('name', first(option.customGroups))} ${option.name}`,
                ])}
              </S.Child>
            ), options)}
          </List>
        </S.DialogContent>
      </S.Dialog>
    </>
  );
};

ChildrenFilter.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,

  })),
  value: PropTypes.number,
  onSelect: PropTypes.func,
};

ChildrenFilter.defaultProps = {
  options: null,
  value: 0,
  onSelect: () => {},
};

export default ChildrenFilter;
